// © Microsoft Corporation. All rights reserved.

import { ImageServiceConfig, KeyValueArray, UrlUtility, getAppEnvironment } from "@msnews/core";
import { Telemetry, TelemetryProps, TelemetryTypes } from "@msnews/experiences-base";

import { TelemetryBase } from "@msnews/core";
import { ViewsCommonAppErrors } from "@msnews/diagnostics";
import { ViewsStringResource } from "./";
import { format } from "@microsoft/fast-web-utilities";

/**
 * Image resize mode enum
 */
export enum ImageResizeMode {
    /**
     * Default value but the Image Resizer actually replaces it by the scale option (2)
     */
    None = "0",

    /**
     * Maintains aspect ratio as letter box
     */
    Letterbox = "1",

    /**
     * Maintains aspect ratio
     */

    Scale = "2",

    /**
     * Stretches image to fit height and width
     */
    Stretch = "3",

    /**
     * Crops image to fit height and width
     */

    Crop = "4",

    /**
     * Crops the image using focal points
     */
    FocalCrop = "5",

    /**
     * Crop the image using a face auto-detection technique.
     */
    FacialCrop = "6"
}

/**
 * An interface for ImageResizeOptions contract
 *
 * @export
 * @interface ImageResizeOptions
 */
export interface ImageResizeOptions {

    /**
     * Letter-boxing an image while performing a resize.
     * This is only applicable to Facial Crop Mode and Focal-cropping is disabled
     */
    LetterBoxing?: boolean;

    /**
     * Focal-cropping (around a facial area) while performing a resize.
     * This is only applicable to Facial Crop Mode
     */
    FocalCropping?: boolean;

    /**
     * Background color is used to fill in the remaining region
     * This is only applicable to Letter-Box Mode
     */
    BackgroundColor?: string;
}

/**
 * An interface for ImageFocalRegion contract
 *
 * @export
 * @interface ImageFocalRegion
 */
export interface ImageFocalRegion {
    /**
     * the first point's x position of the focal point in an image.
     */
    x1?: number;

    /**
     * the second point's x position of the focal point in an image
     */
    x2?: number;

    /**
     * the first point's y position of the focal point in an image
     */
    y1?: number;

    /**
     * the second point's y position of the focal point in an image
     */
    y2?: number;
}

/**
 * An interface for ViewsImageData contract
 *
 * @export
 * @interface ViewsImageData
 */
export interface ViewsImageData {
    /**
     * Alt text to te rendered for image
     */
    alt: string;

    /**
     * Image attribution details
     */
    attribution?: string;

    /**
     * Image caption
     */
    caption: string;

    /**
     * Image height in pixels
     */
    height?: number;

    /**
     * Image id
     */
    id?: string;

    /**
     * Image src url
     */
    src: string;

    /**
     * Image title
     */
    title?: string;

    /**
     * Image width in pixels
     */
    width?: number;
}

/**
 * An interface for ViewsUrlData contract
 *
 * @export
 * @interface ViewsUrlData
 */
export interface ViewsUrlData {
    /**
     * Document id
     */
    cmsId: string;

    /**
     * Namespace (ar or ss)
     */
    ns: string;

    /**
     * Locale
     */
    locale: string;

    /**
     * Document title
     */
    title?: string;

    /**
     * Vertial key
     */
    vertical: string;

    /**
     * Url sections
     */
    sections?: Array<string>;

    /**
     * Query string
     */
    query?: string;
}

/**
 * Default image width to be used for resizing
 */
export const defaultImageWidth = 300;

/**
 * Default image height to be used for resizing
 */
export const defaultImageHeight = 250;

/**
 * Width for provider logo used in article/gallery header
 */
export const defaultProviderLogoWidth = 140;

/**
 * Height for provider logo used in article/gallery header
 */
export const defaultProviderLogoHeight = 40;

/**
 * Default image quality to be used for resizing
 */
export const defaultImageQuality = 100;

/**
 * Default DPI
 */
export const defaultDpi = 1;

/**
 * Compiled regex key for extracting html tags
 */
export const htmlTagsRegEx = new RegExp("<[^>]*>", "gim");

/**
 * Utility class for views components
 */
export const ViewsUtility = {

    /**
     * Ensure to return Views Image Data Object from given object
     * @returns {any} object
     */
    ensureViewsImageData(object: any): ViewsImageData {
        const imgObj = object || {};
        return <ViewsImageData>{
            alt: imgObj.alt,
            attribution: imgObj.attribution,
            caption: imgObj.caption,
            title: imgObj.title,
            src: imgObj.src || imgObj.url
        };
    },

    /**
     * Compose image url
     * @param id Image Id
     * @param imageServiceUrl The base URL for loading CMS images via CDN
     * @param width Expected width
     * @param height Expected height
     * @returns {string} The image url
     */
    getImageUrl(
        id: string,
        width: number = defaultImageWidth,
        height: number = defaultImageHeight,
        imageServiceUrl: string = ImageServiceConfig.prod): string {

        if (!id) {
            return null;
        }

        return `${imageServiceUrl}${id}?w=${width}&h=${height}&q=60&m=6&u=t&o=t&l=f&f=jpg`;
    },

    /**
     * Append resizing params to image url
     *
     * @param image Image details
     * @param width Expected width
     * @param height Expected height
     * @param dpi DPI of device
     * @param resizeMode Image resize mode
     * @param quality Quality of the resized image in percentage (100 means 100%)
     * @param options Image resize options
     * @returns {string} The updated image url
     */
    getResizedImageUrl(
        image: ViewsImageData,
        width: number = defaultImageWidth,
        height: number = defaultImageHeight,
        dpi: number = defaultDpi,
        resizeMode: ImageResizeMode = ImageResizeMode.None,
        quality: number = defaultImageQuality,
        options: ImageResizeOptions = null,
        focalRegion: ImageFocalRegion = null): string {
        if (image && image.src) {
            let opts = "";

            if (options) {
                if (options.LetterBoxing !== undefined) {
                    opts += "&l=" + (options.LetterBoxing === true ? "t" : "f");
                }

                if (options.FocalCropping !== undefined) {
                    opts += "&o=" + (options.FocalCropping === true ? "t" : "f");
                }

                if (options.BackgroundColor !== undefined) {
                    opts += "&b=" + options.BackgroundColor;
                }
            }

            if (focalRegion) {
                let x = 0;
                let y = 0;

                if (focalRegion.x1 === focalRegion.x2 && focalRegion.y1 === focalRegion.y2) {
                    x = focalRegion.x1;
                    y = focalRegion.y1;
                } else {
                    x = focalRegion.x1 + ((focalRegion.x2 - focalRegion.x1) / 2);
                    y = focalRegion.y1 + ((focalRegion.y2 - focalRegion.y1) / 2);
                }

                if (x >= 0 && y >= 0) {
                    opts += "&x=" + x + "&y=" + y;
                }
            }

            return `${image.src}?w=${width * dpi}&h=${height * dpi}&q=${quality}&m=${resizeMode}` + opts;
        }

        return null;
    },

    /**
     * Get the alt text of the image.
     *
     * @param {string} imageCaption The image caption
     * @param {string} altPrefix Text added to the ALT or used as a default
     * @returns {string} The image alt
     */
    getAltText(imageCaption: string, altPrefix: string = ""): string {
        if (!imageCaption) {
            return altPrefix;
        }

        // Decode and strip HTML tags
        const altText = ViewsUtility.stripHtmlTags(imageCaption);

        // Return the default alt text if the caption is null
        if (altText.length < 1) {
            return altPrefix;
        }

        // If the caption is already entirely contained within the alt text, then return the alt text
        if (altPrefix.includes(altText)) {
            return altPrefix;
        } else if (altText.indexOf(altPrefix) === 0) {
            // If the alt text is already the first few words of the caption, then use the caption
            return altText;
        }

        return `${altPrefix}: ${altText}`;
    },

    /**
     * Get the paragraph text with telemetry injected into partner links.
     *
     * @param {string} marker The marker text with placeholder eg: <a {0} href="" />
     * @param {number} linksCount The total number of links with placeholder
     * @param {TelemetryProps} The telemetry props used to the placeholder
     * @returns {string} the text with telemetry on partner links
     */
    getParagraphText(marker: string, linksCount: number, telemetryProps: TelemetryProps): string {
        let markup = marker;
        if (markup && telemetryProps && linksCount > 0) {
            const partnerLinksCount = linksCount;
            let additionalProps: string[] = [];
            for (let i = 0; i < partnerLinksCount; i++) {
                const telemetry = Telemetry.render(ViewsUtility.getChildTelemetryItem(telemetryProps, "partnerLink", TelemetryTypes.Navigation));
                additionalProps.push(` data-m=${telemetry["data-m"]} data-id=${telemetry["data-id"]} target="_blank" `);
            }
            markup = format(marker, ...additionalProps);
        }
        return markup || "";
    },

    /**
     * Helper to check if list is valid and non-empty
     * @param list List to check
     */
    safeAny(list: any[]): boolean {
        return list && list.length > 0;
    },

    /**
     * Helper for generating telemetry attributes from child elements
     * @param parentTelemetry Telemetry props of parent
     * @param telemetryName Telemetry name
     * @param telemetryType Telemetry type
     */
    getChildTelemetryItem(parentTelemetry: TelemetryProps, telemetryName: string, telemetryType: TelemetryTypes): TelemetryProps {
        const initTelemetryProps: TelemetryProps = {
            name: telemetryName,
            telemetryType: telemetryType
        };

        const resultTelemetryProps = Telemetry.createProps(initTelemetryProps, parentTelemetry, initTelemetryProps);
        Telemetry.setChildProps(parentTelemetry, resultTelemetryProps);
        return resultTelemetryProps;
    },

    /**
     * Generate friendly timestamp text based on document publish time
     *
     * @param dateTime Document publish date time
     * @returns {string} Friendly timestamp text
     * @returns {string} Locale specific friendly time stamp
     */
    getFriendlyTimestamp(dateTime: string): string {
        const currentTime = Date.now();
        const publishTime = Date.parse(dateTime);

        if (isNaN(publishTime)) {
            return;
        }

        const timeSpanInSeconds = Math.trunc((currentTime - publishTime) / 1000);
        if (timeSpanInSeconds > 0 && timeSpanInSeconds < 60) {
            return timeSpanInSeconds === 1 ? `${timeSpanInSeconds} ${ViewsStringResource.publishedSecondAgo}` : `${timeSpanInSeconds} ${ViewsStringResource.publishedSecondsAgo}`;
        }

        const timeSpanInMinutes = Math.trunc(timeSpanInSeconds / 60);
        if (timeSpanInMinutes > 0 && timeSpanInMinutes < 60) {
            return timeSpanInMinutes === 1 ? `${timeSpanInMinutes} ${ViewsStringResource.publishedMinuteAgo}` : `${timeSpanInMinutes} ${ViewsStringResource.publishedMinutesAgo}`;
        }

        const timeSpanInHours = Math.trunc(timeSpanInMinutes / 60);
        if (timeSpanInHours > 0 && timeSpanInHours < 24) {
            return timeSpanInHours === 1 ? `${timeSpanInHours} ${ViewsStringResource.publishedHourAgo}` : `${timeSpanInHours} ${ViewsStringResource.publishedHoursAgo}`;
        }

        const timeSpanInDays = Math.trunc(timeSpanInHours / 24);
        if (timeSpanInDays > 0 && timeSpanInDays < 7) {
            return timeSpanInDays === 1 ? `${timeSpanInDays} ${ViewsStringResource.publishedDayAgo}` : `${timeSpanInDays} ${ViewsStringResource.publishedDaysAgo}`;
        }

        const locale: string = getAppEnvironment().CurrentMarket;
        return new Date(dateTime).toLocaleDateString(locale);
    },

    /**
     * Get url data from url string
     * @param {string} url (eg: https://www.msn.com/en-us/news/politics/opinions-title/ar-BBTDXc5?li=AAggNb9)
     * @returns {ViewsUrlData} Url Data generated from url string
     */
    getViewsUrlData(url: string): ViewsUrlData {

        let result: ViewsUrlData = {
            cmsId: "",
            locale: "",
            vertical: "",
            ns: ""
        };

        if (UrlUtility.isValidUrl(url, true) === true) {
            let parser = document.createElement("a");
            parser.href = url;

            if (parser.pathname.length > 0) {
                let parts = parser.pathname.split("/");
                if (parts.length > 4) {
                    result.locale = parts[1];
                    result.vertical = parts[2];
                    result.title = parts[parts.length - 2];
                    let nsParts = parts[parts.length - 1].split("-");
                    if (nsParts.length === 2) {
                        result.ns = nsParts[0];
                        result.cmsId = nsParts[1];
                    }

                    if (parts.length > 4) {
                        result.sections = parts[3].split("/");
                    }
                }
            }
            result.query = parser.search;
        }

        return result;
    },

    /**
     * Get the friendly title string that can be used in url path (eg: this-is-good-title-for-url)
     * @param {ViewsUrlData} title Incoming title
     * @returns {string} String of title from article or gallery (ie: This is good title)
     */
    getFriendlyTitleForUrl(title: string): string {
        if (!title) {
            return title;
        }

        const charReplaceTable: KeyValueArray<string, string> = [
            { key: "#", value: "number" },
            { key: "%", value: "percent" },
            { key: "&", value: "and" },
            { key: "+", value: "plus" },
            { key: "=", value: "equals" },
            { key: "$", value: "dollar" },
            { key: "©", value: "-c-" },
            { key: "®", value: "-r-" },
            { key: "™", value: "-tm-" },
            { key: "¼", value: "1-4" },
            { key: "½", value: "1-2" },
            { key: "¾", value: "3-4" },
            { key: "@", value: "-at-" }
        ];

        const charRegExp = new RegExp("[" + charReplaceTable.map(pair => { return pair.key; }).join("") + "]", "g");
        const removeRegExp = new RegExp("[" + "\"<>*:.?^;`{},'!" + "]", "g");
        const dashRegExp = new RegExp("[" + " |\/\\_~()" + "]", "g");
        let urlTitle = title.replace(removeRegExp, "");
        urlTitle = urlTitle.replace(dashRegExp, "-");
        urlTitle = urlTitle.replace(charRegExp, function (c: string): string {
            return (charReplaceTable.filter(cc => cc.key === c)[0] || { value: c }).value;
        });

        return urlTitle.replace(/[-]{2,}/g, "-").toLocaleLowerCase();
    },

    /**
     * Get the url path for views article or gallery
     * @param {ViewsUrlData} urlData Url details data
     * @returns {string} Url path extract from url data (ie: /en-us/news/good-news/the-title/ar-GBSD1B0?li=BB8GD11)
     */
    getViewsUrlPath(urlData: ViewsUrlData): string {
        let path: string = "";
        if (urlData && urlData.cmsId && urlData.title &&
            urlData.ns && urlData.vertical && urlData.locale) {

            let urlTitle = this.getFriendlyTitleForUrl(urlData.title);
            path = "/" + urlData.locale + "/" + urlData.vertical;

            if (urlData.sections && urlData.sections.length > 0) {
                path += "/" + urlData.sections.join("/");
            }

            path += "/" + urlTitle + "/";
            path = path.toLocaleLowerCase() + urlData.ns + "-" + urlData.cmsId;

            if (urlData.query) {
                const q = urlData.query;
                path += (q && q[0] === "?") ? q : "?" + q;
            }
        }
        return path;
    },

    /**
     * Decode and strip html tags off markup
     * @param input Input markup text
     * @param replaceWith Optional text to replace html tags with
     */
    stripHtmlTags(input: string, replaceWith: string = ""): string {
        try {
            input = decodeURIComponent(input || "");
        } catch (error) {
            TelemetryBase.trackAppErrorEvent({
                ...ViewsCommonAppErrors.InvalidDocumentContentError,
                message: "Failed to strip HTML Tags. Content cannot be decoded.",
                pb: {
                    customMessage: `Failed to decode "${input}": ${error}.`
                }
            });
        }

        return input.replace(htmlTagsRegEx, replaceWith);
    },

    /**
     * Helper (mixin) for text truncation with (...) ellipses
     * @param numLines Number of line shown before truncation is applied.
     */
    applyTextTruncationStyles(numLines: number): any {
        return {
            display: "-webkit-box",
            "text-overflow": "ellipsis",
            "-webkit-box-orient": "vertical",
            "-webkit-line-clamp": numLines > 0 ? `${numLines}` : "none"
        };
    },

    /**
     * Detects if an HTML element is in the viewport
     */
    isInViewport(
        element: React.RefObject<HTMLElement>,
        inViewportPercentage: number,
        inViewportTopOffset: number = 0,
        inViewportBottomOffset: number = 0,
        allowZeroHeight: boolean = false
    ): boolean {
        const currentReference = element.current;

        if (currentReference) {
            const bounds = currentReference.getBoundingClientRect();

            const height = bounds.height;

            if (!allowZeroHeight && !height) {
                return false;
            }

            const top = bounds.top || 0;
            const bottom = bounds.bottom || 0;

            return (top + (height * (1 - inViewportPercentage)) - inViewportTopOffset >= 0 &&
                bottom - (height * (1 - inViewportPercentage)) + inViewportBottomOffset <= window.innerHeight);
        }

        return false;
    }
};